import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gorunurDersler, SINAV_DERSLERI, konuAlaninda, kazanimSozleri } from '../../lib/dersler';
import { MUSTERI_PUAN_SECENEKLERI } from '../../lib/zorlukSeviye';
import PuanSecici from '../ui/PuanSecici';
import MusteriSoruKarti from './MusteriSoruKarti';

const BOS_SUZGEC = { ders: '', mufredat: '', konu: '', alt: '', kazanim: '', puan: [], gorsel: '', baglam: '', q: '' };
const SECIM = 'px-2 py-1.5 rounded border border-gray-300 bg-white text-xs';

// Ders pilinin öğrenme alanı kuralı (matematik/geometri aynı ağacı paylaşır — lib/dersler SINAV_DERSLERI).
const alanKurali = (ders) => [...SINAV_DERSLERI.TYT, ...SINAV_DERSLERI.AYT].find((d) => d.key === ders)?.ogrenmeAlani || null;

/**
 * Müşteri soru listesi (2026-10-26): süzgeç + 20'lik sayfa + "Daha fazla". Kullanıcı isteği
 * gereği HİÇBİR soru sayısı gösterilmez — sunucu da toplam döndürmez (`daha_var` yalnız).
 */
export default function MusteriSorular({ api, sepet, onSepet, onTopluEkle, mesgul, izinliDersler }) {
  const [s, setS] = useState(BOS_SUZGEC);
  const [araMetni, setAraMetni] = useState('');
  const [agac, setAgac] = useState([]);
  const [liste, setListe] = useState([]);
  const [sayfa, setSayfa] = useState(1);
  const [dahaVar, setDahaVar] = useState(false);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const istekNo = useRef(0);
  const soz = kazanimSozleri(s.mufredat);
  const dersler = gorunurDersler(izinliDersler).filter((d) => d.value);
  const set = (parca) => setS((o) => ({ ...o, ...parca }));

  // Müfredat ağacı (ders seçiliyken). "Tümü" müfredatında MEB ağacı.
  useEffect(() => {
    if (!s.ders) { setAgac([]); return undefined; }
    let iptal = false;
    api.kazanim(s.ders, s.mufredat === 'Maarif_2024' ? 'Maarif_2024' : '')
      .then((d) => { if (!iptal) setAgac((d.konular || []).filter((k) => konuAlaninda(k, alanKurali(s.ders), s.mufredat))); })
      .catch(() => { if (!iptal) setAgac([]); });
    return () => { iptal = true; };
  }, [api, s.ders, s.mufredat]);

  const kazanim = s.kazanim || s.alt || s.konu;
  const sorgu = useMemo(() => ({ ders: s.ders, mufredat: s.mufredat, kazanim, puan: s.puan, gorsel: s.gorsel, baglam: s.baglam, q: s.q }),
    [s.ders, s.mufredat, kazanim, s.puan, s.gorsel, s.baglam, s.q]);

  const getir = useCallback(async (n, ekle) => {
    const no = ++istekNo.current;
    setYukleniyor(true); setHata('');
    try {
      const d = await api.sorular(sorgu, n);
      if (no !== istekNo.current) return;
      setListe((o) => {
        if (!ekle) return d.sorular || [];
        const var_ = new Set(o.map((x) => x.soru_no));
        return [...o, ...(d.sorular || []).filter((x) => !var_.has(x.soru_no))];
      });
      setSayfa(n);
      setDahaVar(Boolean(d.daha_var));
    } catch (e) {
      if (no !== istekNo.current) return;
      setHata(e.message || 'Sorular alınamadı');
      if (!ekle) { setListe([]); setDahaVar(false); }
    }
    if (no === istekNo.current) setYukleniyor(false);
  }, [api, sorgu]);

  // Süzgeç değişince liste baştan (eski sayfaların yanıtı istekNo ile düşer).
  useEffect(() => { getir(1, false); }, [getir]);

  const konu = agac.find((k) => k.kod === s.konu) || null;
  const alt = konu ? (konu.alt_konular || []).find((a) => a.kod === s.alt) || null : null;
  const eklenebilir = liste.filter((x) => !sepet.has(x.soru_no) && !x.gorsel_hata).map((x) => x.soru_no);
  const suzgecVar = JSON.stringify({ ...s, q: s.q.trim() }) !== JSON.stringify(BOS_SUZGEC);

  return (
    <div className="space-y-3">
      <div data-musteri-suzgec className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <select aria-label="Ders" value={s.ders} onChange={(e) => set({ ders: e.target.value, konu: '', alt: '', kazanim: '' })} className={SECIM}>
          <option value="">Tüm dersler</option>
          {['TYT', 'AYT'].map((sinav) => (
            <optgroup key={sinav} label={sinav}>
              {dersler.filter((d) => d.value.startsWith(`${sinav.toLowerCase()}_`)).map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </optgroup>
          ))}
        </select>
        <select aria-label="Müfredat" value={s.mufredat} onChange={(e) => set({ mufredat: e.target.value, konu: '', alt: '', kazanim: '' })} className={SECIM}>
          <option value="">Müfredat: tümü</option>
          <option value="MEB_2018">MEB 2018 (2026-2027 YKS)</option>
          <option value="Maarif_2024">Maarif 2024 (2028 ve sonrası)</option>
        </select>
        {s.ders && agac.length > 0 && (
          <select aria-label="Konu" value={s.konu} onChange={(e) => set({ konu: e.target.value, alt: '', kazanim: '' })} className={`${SECIM} max-w-[16rem]`}>
            <option value="">Konu: tümü</option>
            {agac.map((k) => <option key={k.kod} value={k.kod}>{k.kod} {k.ad}</option>)}
          </select>
        )}
        {konu && (konu.alt_konular || []).length > 0 && (
          <select aria-label="Alt konu" value={s.alt} onChange={(e) => set({ alt: e.target.value, kazanim: '' })} className={`${SECIM} max-w-[16rem]`}>
            <option value="">Alt konu: tümü</option>
            {konu.alt_konular.map((a) => <option key={a.kod} value={a.kod}>{a.kod} {a.ad}</option>)}
          </select>
        )}
        {alt && (alt.kazanimlar || []).length > 0 && (
          <select aria-label={soz.Ad} value={s.kazanim} onChange={(e) => set({ kazanim: e.target.value })} className={`${SECIM} max-w-[18rem]`}>
            <option value="">{soz.Ad}: tümü</option>
            {alt.kazanimlar.map((z) => <option key={z.kod} value={z.kod}>{z.kod} {z.ad}</option>)}
          </select>
        )}
        <PuanSecici deger={s.puan} onDegis={(l) => set({ puan: l })} secenekler={MUSTERI_PUAN_SECENEKLERI} ariaLabel="Puan seviyesi" />
        <select aria-label="Görsel" value={s.gorsel} onChange={(e) => set({ gorsel: e.target.value })} className={SECIM}>
          <option value="">Görsel: tümü</option>
          <option value="var">Şekilli</option>
          <option value="yok">Şekilsiz</option>
        </select>
        <select aria-label="Bağlam" value={s.baglam} onChange={(e) => set({ baglam: e.target.value })} className={SECIM}>
          <option value="">Bağlam grubu: tümü</option>
          <option value="var">Yalnız bağlam grupları</option>
          <option value="yok">Bağlam grubu olmayanlar</option>
        </select>
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); set({ q: araMetni }); }}>
          <input aria-label="Metinde ara" value={araMetni} onChange={(e) => setAraMetni(e.target.value)} placeholder="Metinde ara (en az 2 harf)"
            className="px-2 py-1.5 rounded border border-gray-300 text-xs w-48" />
          <button type="submit" className="px-2 py-1.5 rounded bg-gray-100 text-xs font-semibold text-gray-700 hover:bg-gray-200">Ara</button>
        </form>
        {suzgecVar && (
          <button type="button" data-suzgec-temizle onClick={() => { setS(BOS_SUZGEC); setAraMetni(''); }}
            className="px-2 py-1.5 rounded text-xs text-gray-500 hover:bg-gray-100">✕ Süzgeçleri temizle</button>
        )}
      </div>

      {hata && <div className="p-3 rounded-lg border border-red-300 bg-red-50 text-sm text-red-700">{hata}</div>}

      {eklenebilir.length > 1 && (
        <div className="flex justify-end">
          <button type="button" data-toplu-ekle disabled={mesgul} onClick={() => onTopluEkle(eklenebilir)}
            className="px-3 py-1.5 rounded bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50">
            Listedekilerin hepsini sepete ekle
          </button>
        </div>
      )}

      <div className="space-y-4">
        {liste.map((x) => (
          <MusteriSoruKarti key={x.soru_no} soru={x} api={api} sepette={sepet.has(x.soru_no)} onSepet={onSepet} mesgul={mesgul} />
        ))}
      </div>

      {!yukleniyor && !hata && liste.length === 0 && (
        <div data-bos-liste className="p-8 text-center text-sm text-gray-500 bg-white rounded-xl border border-gray-200">
          Bu süzgeçlere uyan soru yok. Süzgeçleri genişletmeyi deneyin.
        </div>
      )}
      {yukleniyor && <div className="py-4 text-center text-sm text-gray-400">Sorular yükleniyor…</div>}
      {dahaVar && !yukleniyor && (
        <div className="text-center">
          <button type="button" data-daha-fazla onClick={() => getir(sayfa + 1, true)}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50">
            Daha fazla soru
          </button>
        </div>
      )}
    </div>
  );
}
